import { useCallback, useEffect, useState } from 'react'
import { CategoryIcon, reminderLook } from '../components/CategoryIcon'
import { ApiError, api, type CategoryDef, type Reminder } from '../lib/api'
import { useStore } from '../lib/store'
import './Done.css'

const PAGE = 50

/** Local calendar day, so grouping matches the viewer's sense of "today". */
function dayKey(iso: string): string {
  const d = new Date(iso)
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
}

function dayLabel(iso: string): string {
  const d = new Date(iso)
  const today = new Date()
  const yesterday = new Date()
  yesterday.setDate(today.getDate() - 1)

  if (dayKey(iso) === dayKey(today.toISOString())) return 'Today'
  if (dayKey(iso) === dayKey(yesterday.toISOString())) return 'Yesterday'

  return d.toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
    // Only disambiguate the year once we're outside it.
    year: d.getFullYear() === today.getFullYear() ? undefined : 'numeric',
  })
}

const MINUTE = 60_000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

function relativeTime(iso: string): string {
  const delta = Date.now() - new Date(iso).getTime()
  if (delta < MINUTE) return 'just now'
  if (delta < HOUR) return `${Math.floor(delta / MINUTE)}m ago`
  if (delta < DAY) return `${Math.floor(delta / HOUR)}h ago`
  const days = Math.floor(delta / DAY)
  return days === 1 ? '1 day ago' : `${days} days ago`
}

interface Group {
  label: string
  items: Reminder[]
}

function groupByDay(items: Reminder[]): Group[] {
  const groups: Group[] = []
  let key: string | null = null
  for (const item of items) {
    if (!item.completed_at) continue
    const k = dayKey(item.completed_at)
    if (k !== key) {
      groups.push({ label: dayLabel(item.completed_at), items: [] })
      key = k
    }
    groups[groups.length - 1]!.items.push(item)
  }
  return groups
}

export function Done({ onBack, onUpgrade }: { onBack: () => void; onUpgrade: () => void }) {
  const { user, refresh, categories } = useStore()
  const [items, setItems] = useState<Reminder[]>([])
  const [loading, setLoading] = useState(true)
  const [more, setMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  // Delete destroys shared history, so it takes two taps — but an inline "Sure?"
  // rather than window.confirm, which blocks the page and breaks the glass look.
  const [confirming, setConfirming] = useState<string | null>(null)

  const load = useCallback(async (offset: number) => {
    const page = await api.listDone(PAGE, offset)
    setMore(page.length === PAGE)
    setItems((prev) => (offset === 0 ? page : [...prev, ...page]))
  }, [])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    api
      .listDone(PAGE, 0)
      .then((page) => {
        if (cancelled) return
        setItems(page)
        setMore(page.length === PAGE)
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [])

  async function restore(r: Reminder) {
    setBusy(r.id)
    setError(null)
    try {
      await api.reopenReminder(r.id)
      setItems((prev) => prev.filter((i) => i.id !== r.id))
      await refresh()
    } catch (e) {
      if (e instanceof ApiError && e.isTierLimit) {
        onUpgrade()
        return
      }
      // Someone else already removed it — drop the stale row rather than stranding it.
      if (e instanceof ApiError && e.status === 404) {
        setItems((prev) => prev.filter((i) => i.id !== r.id))
        return
      }
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(null)
    }
  }

  async function remove(r: Reminder) {
    setBusy(r.id)
    setError(null)
    try {
      await api.deleteReminder(r.id)
      setItems((prev) => prev.filter((i) => i.id !== r.id))
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        setItems((prev) => prev.filter((i) => i.id !== r.id))
        return
      }
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(null)
      setConfirming(null)
    }
  }

  async function loadMore() {
    setBusy('more')
    try {
      await load(items.length)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(null)
    }
  }

  const groups = groupByDay(items)

  return (
    <>
      <header className="nav nav--plain">
        <button className="icon-btn" onClick={onBack} aria-label="Back">
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 5l-7 7 7 7" />
          </svg>
        </button>
        <h1>Done</h1>
      </header>

      <div className="scroll">
        {error && (
          <p className="banner banner--error" role="alert">
            {error}
          </p>
        )}

        {loading && <p className="done__loading">Loading&hellip;</p>}

        {!loading && items.length === 0 && !error && (
          <div className="empty">
            <h2>Nothing finished yet</h2>
            <p>Reminders you tick off show up here, with who closed them and when.</p>
          </div>
        )}

        {groups.map((group) => (
          <section key={group.label} className="done__group">
            <h2 className="done__day">{group.label}</h2>
            <div className="stack">
              {group.items.map((r, i) => (
                <DoneRow
                  key={r.id}
                  reminder={r}
                  index={i}
                  categories={categories}
                  isMe={r.completed_by === user?.id}
                  busy={busy === r.id}
                  confirming={confirming === r.id}
                  onRestore={() => restore(r)}
                  onDelete={() =>
                    confirming === r.id ? remove(r) : setConfirming(r.id)
                  }
                  onCancelDelete={() => setConfirming(null)}
                />
              ))}
            </div>
          </section>
        ))}

        {more && (
          <button
            className="btn btn--ghost done__more"
            disabled={busy === 'more'}
            onClick={loadMore}
          >
            {busy === 'more' ? 'Loading…' : 'Load more'}
          </button>
        )}
      </div>
    </>
  )
}

function DoneRow({
  reminder,
  index,
  categories,
  isMe,
  busy,
  confirming,
  onRestore,
  onDelete,
  onCancelDelete,
}: {
  reminder: Reminder
  index: number
  categories: CategoryDef[]
  isMe: boolean
  busy: boolean
  confirming: boolean
  onRestore: () => void
  onDelete: () => void
  onCancelDelete: () => void
}) {
  const shared = reminder.household_id !== null
  const when = reminder.completed_at ? relativeTime(reminder.completed_at) : ''
  const cat = reminderLook(reminder, categories)

  // "by you" is noise on a personal reminder, but on a shared one the identity is
  // the whole point — so it stays even when the person is you.
  const who = reminder.completed_by_name
    ? shared
      ? ` · by ${isMe ? 'you' : reminder.completed_by_name}`
      : ''
    : ''

  return (
    <article
      className={`dr glass rise${busy ? ' is-busy' : ''}`}
      style={
        {
          animationDelay: `${Math.min(index, 8) * 45}ms`,
          '--cat': cat.color,
        } as React.CSSProperties
      }
    >
      <span className="dr__icon" aria-hidden="true">
        <CategoryIcon icon={cat.icon} color={cat.color} />
        <svg className="dr__tick" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 12.5 9.5 18 20 6.5" />
        </svg>
      </span>

      <div className="dr__body">
        <h3>{reminder.title}</h3>
        <p className="dr__meta">
          {reminder.merchant_label}
          {who}
          {when && `, ${when}`}
        </p>
      </div>

      <div className="dr__actions">
        <button className="dr__act" disabled={busy} onClick={onRestore}>
          Restore
        </button>
        <button
          className={`dr__act dr__act--danger${confirming ? ' is-confirming' : ''}`}
          disabled={busy}
          onClick={onDelete}
          onBlur={onCancelDelete}
          aria-label={confirming ? `Confirm delete ${reminder.title}` : `Delete ${reminder.title}`}
        >
          {confirming ? 'Sure?' : 'Delete'}
        </button>
      </div>
    </article>
  )
}
